import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { CalendarDays, Clock, CheckCircle2, XCircle } from "lucide-react";
import { getAppointments } from "../../../api/api";
import { Card, CardHeader, CardTitle, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";

const getGreeting = (now) => {
  const hour = now.hour();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

// A pure doctor account's home page — today's appointment count + list,
// scoped to their own doctor_id (enforced server-side regardless of what's
// sent here). Distinct from "My Appointments" (the full filterable history);
// this is the at-a-glance equivalent of the clinic-wide Dashboard, which a
// doctor account can't use since its summary endpoint is caredesk/finance/
// master only.
export default function DoctorHome() {
  const doctorId = useSelector((s) => s.users.user?.doctor_id);
  const [appointments, setAppointments] = useState([]);
  const [counts, setCounts] = useState({ upcoming: 0, done: 0, cancelled: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => moment());
  const navigate = useNavigate();

  const today = moment().format("YYYY-MM-DD");

  useEffect(() => {
    if (!doctorId) return;
    setLoading(true);
    getAppointments({ doctor_id: doctorId, from: today, to: today, limit: 100 })
      .then((res) => {
        setAppointments(res.data);
        setCounts(res.counts);
      })
      .catch((err) => toast.error(err?.message || "Could not load today's appointments."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const CARDS = [
    { key: "all", label: "Today's Appointments", icon: CalendarDays },
    { key: "upcoming", label: "Still to see", icon: Clock },
    { key: "done", label: "Completed", icon: CheckCircle2 },
    { key: "cancelled", label: "Cancelled", icon: XCircle },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">{now.format("dddd · D MMMM YYYY · h:mm:ss A")}</p>
          <h2 className="font-serif text-3xl font-semibold">{getGreeting(now)}</h2>
        </div>
        <Button onClick={() => navigate("/my-booking")}>Book a patient</Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.key}>
              <CardContent className="flex items-center gap-3 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-serif text-xl font-semibold">{loading ? "—" : counts[c.key] ?? 0}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Today's patients</CardTitle>
          <button onClick={() => navigate("/my-appointments")} className="text-sm text-primary">
            View all
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          {!loading && appointments.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No appointments today.</p>
          )}
          {appointments
            .slice()
            .sort((a, b) => a.slot_minutes - b.slot_minutes)
            .map((a) => (
              <div key={a._id} className="flex items-center justify-between rounded-md border border-border p-3">
                <div className="flex items-center gap-3">
                  <span className="w-16 shrink-0 text-sm font-medium">{a.slot_time}</span>
                  <div>
                    <p className="text-sm font-medium">{a.patient_id?.name}</p>
                    <p className="text-xs text-muted-foreground">{a.patient_id?.phone}</p>
                  </div>
                </div>
                <Badge variant="outline" className="capitalize">{a.status}</Badge>
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
